import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export const HERO_VIDEO_POSTER_WEBP = "/videos/bidmio-hero-poster.webp";
const HERO_VIDEO_WEBM = "/videos/bidmio-hero.webm";
const HERO_VIDEO_MP4 = "/videos/bidmio-hero.mp4";
const HERO_VIDEO_WEBM_1080P = "/videos/bidmio-hero-1080p.webm";
const HERO_VIDEO_MP4_1080P = "/videos/bidmio-hero-1080p.mp4";
const HERO_VIDEO_POSTER = "/videos/bidmio-hero-poster.jpg";
const HERO_VIDEO_POSTER_2X = "/videos/bidmio-hero-poster@2x.jpg";
const HERO_VIDEO_POSTER_WEBP_2X = "/videos/bidmio-hero-poster@2x.webp";
/** 1×1 transparent GIF: phones match it first, so they never download the poster. */
const BLANK_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

type NetworkInfo = EventTarget & { saveData?: boolean; effectiveType?: string };

function isSlowConnection(conn: NetworkInfo | undefined): boolean {
  if (!conn) return false;
  return (
    conn.saveData === true ||
    conn.effectiveType === "2g" ||
    conn.effectiveType === "slow-2g" ||
    conn.effectiveType === "3g"
  );
}

interface VideoMode {
  video: boolean;
  highRes: boolean;
  highResPoster: boolean;
}

/**
 * Decides whether this placement may stream the muted loop. `placement` is a
 * media query so the desktop backdrop and the mobile frame never both load it.
 * Small screens, slow or save-data connections and reduced motion keep the
 * poster only — the same rules the original video hero used.
 */
function useVideoMode(placement: string): VideoMode {
  const [mode, setMode] = useState<VideoMode>({ video: false, highRes: false, highResPoster: false });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const queries = {
      placement: window.matchMedia(placement),
      small: window.matchMedia("(max-width: 639px)"),
      reduced: window.matchMedia("(prefers-reduced-motion: reduce)"),
      wide: window.matchMedia("(min-width: 1440px)"),
      highDpr: window.matchMedia("(min-resolution: 2dppx)"),
    };
    const conn = (navigator as Navigator & { connection?: NetworkInfo }).connection;

    const evaluate = () => {
      const slow = isSlowConnection(conn);
      const video =
        queries.placement.matches && !queries.small.matches && !queries.reduced.matches && !slow;
      setMode({
        video,
        highRes: video && queries.wide.matches,
        highResPoster: !queries.small.matches && (queries.highDpr.matches || queries.wide.matches),
      });
    };

    evaluate();
    const list = Object.values(queries);
    list.forEach((mq) => mq.addEventListener?.("change", evaluate));
    conn?.addEventListener?.("change", evaluate);
    return () => {
      list.forEach((mq) => mq.removeEventListener?.("change", evaluate));
      conn?.removeEventListener?.("change", evaluate);
    };
  }, [placement]);

  return mode;
}

interface HeroBackdropVideoProps {
  /** Plays while true, pauses otherwise. */
  active: boolean;
  /** Media query this placement is visible at. */
  placement: string;
  className?: string;
}

/** Muted, decorative product loop over its poster. */
export function HeroBackdropVideo({ active, placement, className }: HeroBackdropVideoProps) {
  const { video, highRes, highResPoster } = useVideoMode(placement);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    if (!active) {
      element.pause();
      return;
    }
    element.muted = true;
    void element.play().catch(() => {
      // The poster stays visible when the browser declines autoplay.
    });
  }, [active, video, highRes]);

  return (
    <div className={cn("hero-video-media", className)} aria-hidden="true">
      <picture>
        <source media="(max-width: 639px)" srcSet={BLANK_IMAGE} />
        <source
          type="image/webp"
          media="(min-width: 640px)"
          srcSet={`${HERO_VIDEO_POSTER_WEBP} 1x, ${HERO_VIDEO_POSTER_WEBP_2X} 2x`}
        />
        <source media="(min-width: 640px)" srcSet={`${HERO_VIDEO_POSTER} 1x, ${HERO_VIDEO_POSTER_2X} 2x`} />
        <img src={HERO_VIDEO_POSTER} alt="" loading="eager" decoding="async" />
      </picture>
      {video && (
        <video
          key={highRes ? "hero-1080p" : "hero-720p"}
          ref={videoRef}
          data-testid="hero-product-video"
          poster={highResPoster ? HERO_VIDEO_POSTER_2X : HERO_VIDEO_POSTER}
          autoPlay={active}
          muted
          loop
          playsInline
          preload="metadata"
          tabIndex={-1}
        >
          <source src={highRes ? HERO_VIDEO_WEBM_1080P : HERO_VIDEO_WEBM} type="video/webm" />
          <source src={highRes ? HERO_VIDEO_MP4_1080P : HERO_VIDEO_MP4} type="video/mp4" />
        </video>
      )}
    </div>
  );
}
